import { httpClient } from "@coworker/http-client";
import {
  AskRequest,
  AskResponse,
  AskResponseKind,
  AnyActionCard,
} from "../types/ask";

export async function askQuestion(payload: AskRequest): Promise<AskResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30_000);

  // The service returns a Server-Sent Events (SSE) stream.
  // Events: "data" (query result), "summary", "cards" (immediate action cards),
  // "too_large", "timeout", "empty" (special server-side conditions), "done".
  const text = await httpClient.roomsettings
    .post<string>("/llm/ask", {
      body: payload,
      responseType: "text",
      signal: controller.signal,
    })
    .finally(() => clearTimeout(timeoutId));

  const result: Partial<AskResponse> = {};
  let currentEvent = "";
  const SPECIAL_KINDS: AskResponseKind[] = ["too_large", "timeout", "empty"];

  for (const line of text.split("\n")) {
    if (line.startsWith("event: ")) {
      currentEvent = line.slice("event: ".length).trim();
      if (SPECIAL_KINDS.includes(currentEvent as AskResponseKind)) {
        result.kind = currentEvent as AskResponseKind;
      }
    } else if (line.startsWith("data: ")) {
      const json = line.slice("data: ".length).trim();
      if (!json || json === "{}") continue;
      try {
        const parsed = JSON.parse(json);
        if (currentEvent === "cards") {
          // Dedicated cards event or embedded cards array — accumulate across multiple events
          const incoming: AnyActionCard[] = parsed.cards ?? parsed;
          result.cards = [...(result.cards ?? []), ...incoming];
        } else if (currentEvent === "summary" || parsed.summary !== undefined) {
          result.summary = parsed.summary ?? parsed;
        } else {
          // Main data event — merge top-level fields, explicitly excluding cards
          const { cards: _ignored, ...safeData } = parsed;
          Object.assign(result, safeData);
        }
      } catch {
        // ignore unparseable lines
      }
    }
  }

  return result as AskResponse;
}
